import React, { useEffect, useRef, useState } from "react";
import { Button, Form, Input } from "antd";
import { PlusOutlined } from "@ant-design/icons";

import Icons from "components/icons";
import { Localize } from "components/service";
import CommentsBackendHelper from "../helpers/comments-backend-helper";


const CommentEditForm = ({
    comment,
    onSubmit,
    onCancel,
    onAddAttachment,
    onRemoveAttachment,
    loading = false,
    loadingAttachmentCreate = false,
    loadingAttachmentDelete = false,
    errorText = "",
}) => {
    const [ form ] = Form.useForm();
    const fileInputRef = useRef(null);
    const [ pendingFile, setPendingFile ] = useState(null);
    const attachmentsCountRef = useRef(0);

    const isEditMode = !!comment?.id;
    const attachments = comment?.attachments || [];
    const canAddAttachment = attachments.length < 3;

    useEffect(() => {
        form.setFieldsValue({ text: comment?.text || "" });
    }, [ comment?.id, comment?.text, form ]);

    useEffect(() => {
        if (!loadingAttachmentCreate) {
            setPendingFile(null);
        }
    }, [ loadingAttachmentCreate ]);

    const triggerFileSelect = () => {
        if (!canAddAttachment || loading || loadingAttachmentCreate) {
            return;
        }
        fileInputRef.current?.click();
    };

    const handleFileSelect = (event) => {
        const file = event.target.files?.[ 0 ];
        event.target.value = "";
        if (!file || !canAddAttachment) {
            return;
        }
        attachmentsCountRef.current = attachments.length;
        setPendingFile({ name: file.name, done: false });
        onAddAttachment(file);
    };

    return (
        <Form
            key={ comment?.id || "new-comment-form" }
            layout="vertical"
            form={ form }
            className="model-form edit-model-form comments-edit-form"
            onFinish={ (values) => {
                onSubmit({
                    text: values.text,
                    attachments,
                });
            } }
        >
            <Form.Item
                name="text"
                rules={ [ { required: true } ] }
            >
                <Input.TextArea
                    autoSize={ { minRows: 5, maxRows: 8 } }
                    disabled={ loading }
                />
            </Form.Item>

            <ul className="comments-form-attachments">
                { attachments.map((attachment) => {
                    const { fileName, extension } = CommentsBackendHelper.getAttachmentNameParts(attachment);
                    const fullName = extension ? `${ fileName }.${ extension }` : fileName;

                    return (
                        <li className="comments-form-attachment-item" key={ attachment.id }>
                        { attachment.path ? (
                            <a
                                className="comments-form-attachment-name"
                                href={ attachment.path }
                                target="_blank"
                                rel="noreferrer"
                                title={ fullName }
                            >
                                <Icons.Attachment />
                                <span className="download-file-info">
                                    <span className="download-file-name">
                                        <strong className="file-name">{ fileName }</strong>
                                        { extension && <strong>.{ extension }</strong> }
                                    </span>
                                </span>
                            </a>
                        ) : (
                            <span className="comments-form-attachment-name" title={ fullName }>
                                <Icons.Attachment />
                                <span className="download-file-info">
                                    <span className="download-file-name">
                                        <strong className="file-name">{ fileName }</strong>
                                        { extension && <strong>.{ extension }</strong> }
                                    </span>
                                </span>
                            </span>
                        ) }
                        <button
                            type="button"
                            className="comments-form-attachment-remove"
                            disabled={ loading || loadingAttachmentDelete }
                            onClick={ () => onRemoveAttachment(attachment.id) }
                        >
                            <Icons.Delete />
                        </button>
                        </li>
                    );
                }) }
                { pendingFile && attachments.length === attachmentsCountRef.current && (() => {
                    const { fileName, extension } = CommentsBackendHelper.getAttachmentNameParts({ name: pendingFile.name });
                    return (
                        <li className="comments-form-attachment-item comments-form-attachment-item--uploading">
                            <div className="comments-form-attachment-uploading-row">
                                <span className="comments-form-attachment-name">
                                    <Icons.Attachment />
                                    <span className="download-file-info">
                                        <span className="download-file-name">
                                            <strong className="file-name">{ fileName }</strong>
                                            { extension && <strong>.{ extension }</strong> }
                                        </span>
                                    </span>
                                </span>
                                <span className="comments-uploading-label"><Localize>COMMENTS.Text_Uploading</Localize></span>
                            </div>
                            <div className="comments-upload-progress-bar">
                                <div className={ `comments-upload-progress-fill${ pendingFile.done ? " done" : "" }` } />
                            </div>
                        </li>
                    );
                })() }
            </ul>

            { canAddAttachment && (
                <>
                    <input
                        ref={ fileInputRef }
                        type="file"
                        style={ { display: "none" } }
                        onChange={ handleFileSelect }
                    />
                    <button
                        type="button"
                        className="comments-add-file-link"
                        onClick={ triggerFileSelect }
                        disabled={ loading || loadingAttachmentCreate }
                    >
                        <PlusOutlined /> <Localize>GLOBAL.Button_Text_AddFile</Localize>
                    </button>
                </>
            ) }

            { !!errorText && (
                <div className="comments-form-error">{ errorText }</div>
            ) }

            <div className="form-btn-holder">
                <Button
                    className="ant-btn ant-btn-secondary bg-light-gray"
                    onClick={ onCancel }
                    disabled={ loading }
                >
                    <Localize>GLOBAL.Button_Text_Cancel</Localize>
                </Button>

                <Button
                    className="btn-right"
                    type="primary"
                    htmlType="submit"
                    loading={ loading }
                >
                    { isEditMode ? <Localize>COMMENTS.Button_Text_Save</Localize> : <Localize>COMMENTS.Button_Text_AddUpper</Localize> }
                </Button>
            </div>
        </Form>
    );
};

export default CommentEditForm;
